import { Location } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, isDevMode } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import {
  LocalizeParser,
  LocalizeRouterSettings,
  ManualParserLoader,
  withLocalizeRouter
} from '@gilsdav/ngx-translate-router';
import { LocalizeRouterHttpLoader } from '@gilsdav/ngx-translate-router-http-loader';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
// Import all Froala Editor plugins.
import 'froala-editor/js/plugins.pkgd.min.js';
import 'froala-editor/js/third_party/spell_checker.min';
import 'froala-editor/js/languages/nl.js'; // Import a Froala Editor language file.
import {
  CUSTOM_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  customBearerTokenInterceptor,
  UserActivityService
} from 'keycloak-angular';
import moment from 'moment';
import 'moment/locale/nl-be';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { AppRouteReuseStrategy } from './app-route-reuse-strategy.class';
import { APP_ROUTES } from './app.routing';
import { CoreModule } from './core/core.module';
import { RepositoriesModule } from './repositories/repositories.module';
import stylingPreset from './stylingPreset';
import { TranslationsLoader } from './translations.loader';
import { UiModule } from './ui/ui.module';
import { UserService } from './user.service';

export function LocalizeRouterHttpLoaderFactory(
  translate: TranslateService,
  location: Location,
  settings: LocalizeRouterSettings,
  http: HttpClient
) {
  return new LocalizeRouterHttpLoader(translate, location, settings, http);
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslationsLoader(http);
}

export function createTranslateRouteLoader(
  translate: TranslateService,
  location: Location,
  settings: LocalizeRouterSettings
) {
  return new ManualParserLoader(translate, location, settings, ['nl'], 'ROUTES.');
}

export function momentAdapterFactory() {
  return adapterFactory(moment);
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: CUSTOM_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [
        {
          shouldAddToken: async (req, _, keycloak) => {
            return keycloak.authenticated;
          }
        }
      ]
    },
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
    provideHttpClient(withInterceptors([customBearerTokenInterceptor])),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient]
        }
      }),
      StoreModule.forRoot({}),
      EffectsModule.forRoot([]),
      StoreRouterConnectingModule.forRoot(),
      StoreDevtoolsModule.instrument({
        maxAge: 25, // Retains last 25 states
        logOnly: !isDevMode(), // Restrict extension to log-only mode
        autoPause: true, // Pauses recording actions and state changes when the extension window is not open
        trace: true, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
        traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
        connectInZone: true
      }),

      CalendarModule.forRoot({
        provide: DateAdapter,
        useFactory: momentAdapterFactory
      }),
      FroalaEditorModule.forRoot(),
      FroalaViewModule.forRoot(),
      RepositoriesModule,
      CoreModule,
      UiModule,
      UserService
    ),

    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: stylingPreset,
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng, myTheme'
          }
        }
      },
      zIndex: {
        modal: 1100, // dialog, sidebar
        overlay: 1000, // dropdown, overlaypanel
        menu: 1000, // overlay menus
        tooltip: 1100 // tooltip
      }
    }),
    provideRouter(
      APP_ROUTES,
      withLocalizeRouter(APP_ROUTES, {
        parser: {
          provide: LocalizeParser,
          useFactory: LocalizeRouterHttpLoaderFactory,
          deps: [TranslateService, Location, LocalizeRouterSettings, HttpClient]
        },
        initialNavigation: true,
        alwaysSetPrefix: false
      })
    ),
    UserActivityService,
    MessageService
  ]
};
